/**
 * Scheda giocatore — Statistiche (V40), nella colonna destra di Generale.
 *
 * I totali del periodo scelto in una tabella a due colonne, voce e valore:
 * ogni voce con l'icona e il colore dei suoi eventi, la media voto con il
 * colore della sua fascia.
 */

import type { ReactNode } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { ClockIcon, ShirtIcon, StarIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { StatoCaricamento } from '../comuni/Testata.js';
import { useDati } from '../comuni/dati.js';
import { NonTrovata } from '../navigazione/NonTrovata.js';
import { voce } from '../navigazione/voci.js';
import { IconaEvento } from '../partite/eventi.js';
import { coloreVoto } from './colori-stato.js';
import { FiltriScheda } from './FiltriScheda.js';
import {
  type Metriche,
  type StatisticheGiocatore as Dati,
  filtriDaSearch,
  queryFiltri,
} from './elenchi.js';

export function StatisticheGiocatore() {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  const filtri = filtriDaSearch(search);
  // Cambiando filtro restano i numeri di prima finche arrivano i nuovi: la
  // card non sparisce e il calendario aperto resta aperto.
  const { dati, errore, stato } = useDati<Dati>(
    `/giocatori/${id}/statistiche${queryFiltri(filtri)}`,
    { tieni: true },
  );

  if (stato === 404) return <NonTrovata />;
  if (!dati) return <StatoCaricamento errore={errore} />;

  const Icona = voce('statistiche').icona;
  // Una card sola che riempie la colonna, alta come quelle a sinistra.
  return (
    <Card className="flex-1 bg-muted">
      <CardHeader>
        <CardTitle className="flex items-center justify-center gap-2 font-semibold tracking-wide uppercase">
          <Icona aria-hidden className="size-5" />
          <h2>Statistiche</h2>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 px-2 @xl:px-(--card-spacing)">
        <FiltriScheda disponibili={dati.filtriDisponibili} />
        {dati.perSquadra.length === 0 ? (
          <Empty className="rounded-lg border bg-card">
            <EmptyHeader>
              <EmptyTitle>Nessuna partita conclusa</EmptyTitle>
              <EmptyDescription>
                Gol, minuti e presenze si calcolano dalle partite concluse.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <Totali m={dati.totale} />
        )}
      </CardContent>
    </Card>
  );
}

function n(v: number) {
  return v.toLocaleString('it-IT');
}

/** Presenze, minuti e voto non sono eventi: hanno un'icona loro, neutra. */
const NEUTRA = 'size-4 shrink-0 text-evento-neutro';

function Totali({ m }: { m: Metriche }) {
  const righe: { etichetta: string; icona: ReactNode; valore: ReactNode }[] = [
    {
      etichetta: 'Presenze',
      icona: <ShirtIcon aria-hidden className={NEUTRA} />,
      valore: n(m.presenze),
    },
    {
      etichetta: 'Minuti',
      icona: <ClockIcon aria-hidden className={NEUTRA} />,
      valore: n(m.minuti),
    },
    { etichetta: 'Gol', icona: <IconaEvento tipo="gol" />, valore: n(m.gol) },
    { etichetta: 'Assist', icona: <IconaEvento tipo="assist" />, valore: n(m.assist) },
    { etichetta: 'Autogol', icona: <IconaEvento tipo="autogol" />, valore: n(m.autogol) },
    {
      etichetta: 'Tiri in porta',
      icona: <IconaEvento tipo="tiro_specchio" />,
      valore: n(m.tiriSpecchio),
    },
    { etichetta: 'Tiri fuori', icona: <IconaEvento tipo="tiro_fuori" />, valore: n(m.tiriFuori) },
    {
      etichetta: 'Passaggi riusciti',
      icona: <IconaEvento tipo="passaggio_completato" />,
      valore: n(m.passaggiCompletati),
    },
    {
      etichetta: 'Passaggi sbagliati',
      icona: <IconaEvento tipo="passaggio_sbagliato" />,
      valore: n(m.passaggiSbagliati),
    },
    {
      etichetta: 'Palle recuperate',
      icona: <IconaEvento tipo="palla_conquistata" />,
      valore: n(m.palleConquistate),
    },
    {
      etichetta: 'Palle perse',
      icona: <IconaEvento tipo="palla_persa" />,
      valore: n(m.pallePerse),
    },
    { etichetta: 'Gialli', icona: <IconaEvento tipo="cartellino_giallo" />, valore: n(m.gialli) },
    { etichetta: 'Rossi', icona: <IconaEvento tipo="cartellino_rosso" />, valore: n(m.rossi) },
  ];
  // Il Dirigente non legge i voti: la riga non arriva nemmeno.
  if ('votoMedio' in m) {
    righe.push({
      etichetta: 'Media voto',
      icona: <StarIcon aria-hidden className={NEUTRA} />,
      valore:
        m.votoMedio == null ? (
          '—'
        ) : (
          <Badge className={cn('min-w-9 tabular-nums', coloreVoto(m.votoMedio))}>
            {m.votoMedio.toLocaleString('it-IT')}
          </Badge>
        ),
    });
  }

  return (
    <div className="rounded-lg border bg-card px-3 py-1">
      <Table>
        <TableBody>
          {righe.map((r) => (
            <TableRow key={r.etichetta}>
              <TableCell>
                <span className="flex items-center gap-2">
                  {r.icona}
                  {r.etichetta}
                </span>
              </TableCell>
              <TableCell className="text-right font-medium tabular-nums">{r.valore}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
